import { getTaskTemplateOptions, listTaskTemplates, type TaskTemplateListType } from '@repo/crm-atoms';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button,
  Card,
  Form,
  Select,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type CompleteTaskNodeData, type CompleteTaskNodeType } from 'automations/types/nodeSchemas/actionNodes';
import { nodeSchemas } from 'automations/types/nodeTypes';
import { saveActionNode } from 'automations/utils/actionNode';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import styles from './CompleteTask.module.scss';

type CompleteTaskContentType = CompleteTaskNodeData['additionalOptions']['completeTask'];
export default function CompleteTask({
  node,
}: {
  node: NodeProps<CompleteTaskNodeType>;
}) {
  const dispatch = useAutomationDispatch();
  const [loading, setLoading] = useState(false);
  const parentId = useSelector(getParentIdSelector(node.id));
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const [form] = Form.useForm<CompleteTaskContentType>();

  const { data: templateData } = useGetGraphqlDataQuery<UseQueryResult<TaskTemplateListType>>({
    document: listTaskTemplates,
  });

  const taskTemplateOptions = useMemo(() => (
    templateData ? getTaskTemplateOptions(templateData) : []
  ), [templateData]);

  useEffect(() => {
    if (node.data?.additionalOptions?.completeTask) {
      form.setFieldsValue(node.data.additionalOptions.completeTask);
    }
  }, [form, node.data?.additionalOptions?.completeTask]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    try {
      const values = await form.validateFields();

      const parsedData = nodeSchemas.CompleteTaskNodeData.parse({
        ...node.data,
        additionalOptions: {
          ...node.data?.additionalOptions,
          completeTask: values,
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedData.additionalOptions,
      }));
      await dispatch(saveActionNode(node.id, parentId, contentMode));
      setMenuOpen(false);
    } catch (error: any) {
      if (error?.errorFields?.length) {
        message.error(error?.errorFields[0]?.errors[0]);
      } else {
        const errorMessage = error instanceof ZodError
          ? 'Add All Fields to Continue'
          : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [contentMode, dispatch, form, node.data, node.id, parentId, setMenuOpen]);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(`Action - Complete #{${TranslationEntity.task}}`, [{ capitalize: true }]),
        destroyOnClose: true,
      }}
    >
      <Card title={contentMode === 'create' ? 'Set Action' : 'Edit Action'}>
        <Form
          form={form}
          layout="horizontal"
          colon={false}
          requiredMark={false}
        >
          <Form.Item
            name="templateIds"
            label="Task Template"
            rules={[{ required: true }]}
          >
            <Select
              mode="multiple"
              options={taskTemplateOptions}
              placeholder="Select task template"
            />
          </Form.Item>
        </Form>
      </Card>

      <Button
        type="primary"
        className={styles.submitButton}
        onClick={handleSubmit}
        loading={loading}
        disabled={loading}
      >
        Set Action
      </Button>
    </NodeMenu>
  );
}
